import React, { useEffect, useState } from "react";
import { API_URL } from "../api";
import { getOptimizedCloudinaryUrl } from "../utils/cloudinary";
import { formatAed, formatBookingDateTime } from "../utils/booking";

const bookingStatusFilters = [
  { value: "paid", label: "Paid" },
  { value: "pending", label: "Pending" },
  { value: "failed", label: "Failed" },
  { value: "expired", label: "Expired" },
  { value: "all", label: "All" },
];

function formatDateOfBirth(date) {
  if (!date) return "";
  return new Date(date).toLocaleDateString("en-GB", {
    timeZone: "UTC",
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
}

function getImage(car) {
  return getOptimizedCloudinaryUrl(car.imageUrl, 240, 160);
}

function Admin() {
  const initialCarForm = {
    companyName: "",
    name: "",
    type: "sedan",
    capacity: "5",
    daily: "",
    monthly: "",
    available: true,
  };
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [token, setToken] = useState(localStorage.getItem("adminToken") || "");
  const [adminEmail, setAdminEmail] = useState(
    localStorage.getItem("adminEmail") || "",
  );
  const [cars, setCars] = useState([]);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [carForm, setCarForm] = useState(initialCarForm);
  const [mainImage, setMainImage] = useState(null);
  const [galleryImages, setGalleryImages] = useState([]);
  const [isCreating, setIsCreating] = useState(false);
  const [carPendingDelete, setCarPendingDelete] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [activeTab, setActiveTab] = useState("cars");
  const [bookings, setBookings] = useState([]);
  const [bookingFilter, setBookingFilter] = useState("paid");
  const [isLoadingBookings, setIsLoadingBookings] = useState(false);

  useEffect(() => {
    if (!token) return;

    fetch(`${API_URL}/admin/cars`, {
      headers: { Authorization: token },
    })
      .then((response) => {
        if (!response.ok) throw new Error("Could not load admin cars");
        return response.json();
      })
      .then(setCars)
      .catch(() => {
        setError("Could not load cars. Check backend server and admin token.");
      });
  }, [token]);

  useEffect(() => {
    if (!token || activeTab !== "bookings") return;

    setIsLoadingBookings(true);
    fetch(`${API_URL}/admin/bookings?status=${bookingFilter}`, {
      headers: { Authorization: token },
    })
      .then((response) => {
        if (!response.ok) throw new Error("Could not load bookings");
        return response.json();
      })
      .then(setBookings)
      .catch(() => {
        setError("Could not load bookings. Check backend server and admin token.");
      })
      .finally(() => setIsLoadingBookings(false));
  }, [token, activeTab, bookingFilter]);

  const handleLogin = async (event) => {
    event.preventDefault();
    setError("");

    try {
      const response = await fetch(`${API_URL}/auth/admin/login`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, password }),
      });
      const data = await response.json();

      if (!response.ok) throw new Error(data.message || "Login failed");

      localStorage.setItem("adminToken", data.token);
      localStorage.setItem("adminEmail", data.email || email);
      window.dispatchEvent(new Event("admin-auth-changed"));
      setToken(data.token);
      setAdminEmail(data.email || email);
    } catch (loginError) {
      setError(loginError.message);
    }
  };

  const handleCarFormChange = (event) => {
    const { name, value, type, checked } = event.target;
    setCarForm((currentForm) => ({
      ...currentForm,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const handleCreateCar = async (event) => {
    event.preventDefault();
    setError("");
    setSuccess("");

    if (!mainImage) {
      setError("Please choose a main car image.");
      return;
    }

    const formData = new FormData();
    Object.entries(carForm).forEach(([key, value]) => {
      formData.append(key, value);
    });
    formData.append("mainImage", mainImage);
    galleryImages.forEach((image) => {
      formData.append("galleryImages", image);
    });

    setIsCreating(true);
    try {
      const response = await fetch(`${API_URL}/admin/cars`, {
        method: "POST",
        headers: { Authorization: token },
        body: formData,
      });
      const data = await response.json();

      if (!response.ok) throw new Error(data.message || "Could not add car");

      setCars((currentCars) => [...currentCars, data]);
      setCarForm(initialCarForm);
      setMainImage(null);
      setGalleryImages([]);
      event.target.reset();
      setSuccess("Car added successfully.");
    } catch (createError) {
      setError(createError.message);
    } finally {
      setIsCreating(false);
    }
  };

  const formatDateForInput = (date) => {
    if (!date) return "";
    const parsed = new Date(date);
    if (Number.isNaN(parsed.getTime())) return "";
    return parsed.toISOString().slice(0, 10);
  };

  const updateCar = async (car, changes) => {
    const nextCar = {
      ...car,
      ...changes,
      rent: { ...car.rent, ...changes.rent },
    };

    const isDateChange =
      changes.availableFrom !== undefined || changes.availableTo !== undefined;

    if (!isDateChange) {
      setCars((currentCars) =>
        currentCars.map((currentCar) =>
          currentCar._id === car._id ? nextCar : currentCar,
        ),
      );
    }

    try {
      const response = await fetch(`${API_URL}/admin/car/${car._id}/manage`, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          Authorization: token,
        },
        body: JSON.stringify({
          daily: nextCar.rent.daily,
          monthly: nextCar.rent.monthly,
          available: nextCar.available,
          availableFrom: nextCar.availableFrom || null,
          availableTo: nextCar.availableTo || null,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        setError(errorData.message || "Update failed. Please try again.");
        return;
      }

      const savedCar = await response.json();
      setCars((currentCars) =>
        currentCars.map((currentCar) =>
          currentCar._id === savedCar._id ? savedCar : currentCar,
        ),
      );
    } catch (error) {
      setError("Update failed. Please try again.");
    }
  };

  const [priceEdits, setPriceEdits] = useState({});

  const getPriceValue = (car, field) => {
    const key = `${car._id}-${field}`;
    return priceEdits[key] !== undefined ? priceEdits[key] : car.rent[field];
  };

  const handlePriceChange = (car, field, value) => {
    const key = `${car._id}-${field}`;
    setPriceEdits((current) => ({ ...current, [key]: value }));
  };

  const handlePriceBlur = (car, field) => {
    const key = `${car._id}-${field}`;
    const value = priceEdits[key];
    setPriceEdits((current) => {
      const next = { ...current };
      delete next[key];
      return next;
    });

    if (value === undefined || value === "" || Number(value) === car.rent[field]) {
      return;
    }
    updateCar(car, { rent: { [field]: Number(value) } });
  };

  const getCarTitle = (car) => `${car.companyName} ${car.name}`.trim();

  const openDeleteDialog = (car) => {
    setError("");
    setSuccess("");
    setCarPendingDelete(car);
  };

  const closeDeleteDialog = () => {
    if (!isDeleting) setCarPendingDelete(null);
  };

  const deleteCar = async () => {
    if (!carPendingDelete) return;
    const car = carPendingDelete;
    const carTitle = `${car.companyName} ${car.name}`.trim();

    setError("");
    setSuccess("");
    setIsDeleting(true);

    try {
      const response = await fetch(`${API_URL}/admin/cars/${car._id}`, {
        method: "DELETE",
        headers: { Authorization: token },
      });
      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || "Could not delete car");
      }

      setCars((currentCars) =>
        currentCars.filter((currentCar) => currentCar._id !== car._id),
      );
      setSuccess(`${carTitle} deleted successfully.`);
      setCarPendingDelete(null);
    } catch (deleteError) {
      setError(deleteError.message);
    } finally {
      setIsDeleting(false);
    }
  };

  const logout = () => {
    localStorage.removeItem("adminToken");
    localStorage.removeItem("adminEmail");
    window.dispatchEvent(new Event("admin-auth-changed"));
    setToken("");
    setAdminEmail("");
    setCars([]);
    setBookings([]);
  };

  if (!token) {
    return (
      <main className="adminPage">
        <form className="adminLogin" onSubmit={handleLogin}>
          <h1>Admin Login</h1>
          <input
            type="email"
            value={email}
            onChange={(event) => setEmail(event.target.value)}
            placeholder="Email"
            required
          />
          <input
            type="password"
            value={password}
            onChange={(event) => setPassword(event.target.value)}
            placeholder="Password"
            required
          />
          {error && <p className="adminError">{error}</p>}
          <button type="submit">Login</button>
        </form>
      </main>
    );
  }

  return (
    <main className="adminPage">
      <div className="adminHeader">
        <div>
          <h1>Admin</h1>
          {adminEmail && <p className="adminSignedIn">Signed in as {adminEmail}</p>}
        </div>
        <button type="button" onClick={logout}>
          Logout
        </button>
      </div>
      <div className="adminTabs" role="tablist">
        {[
          { value: "cars", label: "Cars" },
          { value: "bookings", label: "Bookings" },
        ].map((tab) => (
          <button
            key={tab.value}
            type="button"
            role="tab"
            aria-selected={activeTab === tab.value}
            className={activeTab === tab.value ? "adminTabActive" : ""}
            onClick={() => {
              setError("");
              setSuccess("");
              setActiveTab(tab.value);
            }}
          >
            {tab.label}
          </button>
        ))}
      </div>
      {error && <p className="adminError">{error}</p>}
      {success && <p className="adminSuccess">{success}</p>}
      {activeTab === "bookings" && (
        <section className="adminBookings">
          <div className="adminBookingFilters">
            {bookingStatusFilters.map((filter) => (
              <button
                key={filter.value}
                type="button"
                className={
                  bookingFilter === filter.value ? "adminBookingFilterActive" : ""
                }
                onClick={() => setBookingFilter(filter.value)}
              >
                {filter.label}
              </button>
            ))}
          </div>
          {isLoadingBookings && <p>Loading bookings...</p>}
          {!isLoadingBookings && !bookings.length && <p>No bookings yet.</p>}
          {!isLoadingBookings &&
            bookings.map((booking) => (
              <article className="adminBookingRow" key={booking._id}>
                <div className="adminBookingHeader">
                  <strong>{booking.reference}</strong>
                  <span
                    className={`adminBookingStatus adminBookingStatus-${booking.status}`}
                  >
                    {booking.status}
                  </span>
                  <span className="adminBookingCreated">
                    {formatBookingDateTime(booking.paidAt || booking.createdAt)}
                  </span>
                </div>
                <dl>
                  <div>
                    <dt>Car</dt>
                    <dd>
                      {booking.carSnapshot?.companyName} {booking.carSnapshot?.name}
                    </dd>
                  </div>
                  <div>
                    <dt>Pickup</dt>
                    <dd>{formatBookingDateTime(booking.pickupAt)}</dd>
                  </div>
                  <div>
                    <dt>Return</dt>
                    <dd>{formatBookingDateTime(booking.returnAt)}</dd>
                  </div>
                  <div>
                    <dt>Rent</dt>
                    <dd>
                      {booking.period === "monthly" ? "Monthly" : "Daily"},{" "}
                      {booking.pricing.rentalDays} days
                    </dd>
                  </div>
                  <div>
                    <dt>Customer</dt>
                    <dd>
                      {booking.customer.firstName} {booking.customer.lastName}
                    </dd>
                  </div>
                  <div>
                    <dt>Date of birth</dt>
                    <dd>{formatDateOfBirth(booking.customer.dateOfBirth)}</dd>
                  </div>
                  <div>
                    <dt>Email</dt>
                    <dd>
                      <a href={`mailto:${booking.customer.email}`}>
                        {booking.customer.email}
                      </a>
                    </dd>
                  </div>
                  <div>
                    <dt>Phone</dt>
                    <dd>
                      <a href={`tel:${booking.customer.phone}`}>
                        {booking.customer.phone}
                      </a>
                    </dd>
                  </div>
                  <div>
                    <dt>Total (incl. VAT)</dt>
                    <dd>{formatAed(booking.pricing.total)}</dd>
                  </div>
                </dl>
              </article>
            ))}
        </section>
      )}
      {activeTab === "cars" && (
        <>
          <form className="adminCreateCar" onSubmit={handleCreateCar}>
            <h2>Add New Car</h2>
            <div className="adminFormGrid">
              <label>
                Company
                <input
                  type="text"
                  name="companyName"
                  value={carForm.companyName}
                  onChange={handleCarFormChange}
                  required
                />
              </label>
              <label>
                Model
                <input
                  type="text"
                  name="name"
                  value={carForm.name}
                  onChange={handleCarFormChange}
                  required
                />
              </label>
              <label>
                Type
                <select
                  name="type"
                  value={carForm.type}
                  onChange={handleCarFormChange}
                  required
                >
                  <option value="sedan">Sedan</option>
                  <option value="suv">SUV</option>
                  <option value="sport">Sport</option>
                  <option value="luxury">Luxury</option>
                </select>
              </label>
              <label>
                Capacity
                <input
                  type="number"
                  name="capacity"
                  min="1"
                  value={carForm.capacity}
                  onChange={handleCarFormChange}
                  required
                />
              </label>
              <label>
                Daily price
                <input
                  type="number"
                  name="daily"
                  min="0"
                  value={carForm.daily}
                  onChange={handleCarFormChange}
                  required
                />
              </label>
              <label>
                Monthly price
                <input
                  type="number"
                  name="monthly"
                  min="0"
                  value={carForm.monthly}
                  onChange={handleCarFormChange}
                  required
                />
              </label>
              <label>
                Main image
                <input
                  type="file"
                  accept="image/*"
                  onChange={(event) => setMainImage(event.target.files[0] || null)}
                  required
                />
              </label>
              <label>
                Gallery images
                <input
                  type="file"
                  accept="image/*"
                  multiple
                  onChange={(event) =>
                    setGalleryImages(Array.from(event.target.files || []))
                  }
                />
              </label>
              <label className="adminAvailability">
                Available
                <input
                  type="checkbox"
                  name="available"
                  checked={carForm.available}
                  onChange={handleCarFormChange}
                />
              </label>
            </div>
            <button type="submit" disabled={isCreating}>
              {isCreating ? "Adding..." : "Add car"}
            </button>
          </form>
          <div className="adminCars">
            {cars.map((car) => (
              <section className="adminCarRow" key={car._id}>
                <img
                  src={getImage(car)}
                  alt={car.name}
                  width="120"
                  height="80"
                  loading="lazy"
                  decoding="async"
                />
                <div>
                  <h2>
                    {car.companyName}
                    {car.name}
                  </h2>
                  <p>{car.type}</p>
                </div>
                <label>
                  Daily
                  <input
                    type="number"
                    min="0"
                    value={getPriceValue(car, "daily")}
                    onChange={(event) =>
                      handlePriceChange(car, "daily", event.target.value)
                    }
                    onBlur={() => handlePriceBlur(car, "daily")}
                    onKeyDown={(event) => {
                      if (event.key === "Enter") event.target.blur();
                    }}
                  />
                </label>
                <label>
                  Monthly
                  <input
                    type="number"
                    min="0"
                    value={getPriceValue(car, "monthly")}
                    onChange={(event) =>
                      handlePriceChange(car, "monthly", event.target.value)
                    }
                    onBlur={() => handlePriceBlur(car, "monthly")}
                    onKeyDown={(event) => {
                      if (event.key === "Enter") event.target.blur();
                    }}
                  />
                </label>
                <label className="adminAvailability">
                  Available
                  <input
                    type="checkbox"
                    checked={car.available !== false}
                    onChange={(event) =>
                      updateCar(car, { available: event.target.checked })
                    }
                  />
                </label>
                <button
                  type="button"
                  className="adminDeleteButton"
                  onClick={() => openDeleteDialog(car)}
                >
                  Delete
                </button>
                {car.available === false && (
                  <div className="adminUnavailableDates">
                    <label className="adminUnavailableDate">
                      Unavailable from
                      <input
                        type="date"
                        value={formatDateForInput(car.availableFrom)}
                        onChange={(event) =>
                          updateCar(car, {
                            availableFrom: event.target.value || null,
                          })
                        }
                      />
                    </label>
                    <label className="adminUnavailableDate">
                      Unavailable to
                      <input
                        type="date"
                        value={formatDateForInput(car.availableTo)}
                        onChange={(event) =>
                          updateCar(car, {
                            availableTo: event.target.value || null,
                          })
                        }
                      />
                    </label>
                  </div>
                )}
              </section>
            ))}
          </div>
        </>
      )}
      {carPendingDelete && (
        <div
          className="adminDeleteOverlay"
          role="presentation"
          onMouseDown={closeDeleteDialog}
        >
          <section
            className="adminDeleteDialog"
            role="dialog"
            aria-modal="true"
            aria-labelledby="deleteDialogTitle"
            aria-describedby="deleteDialogText"
            onMouseDown={(event) => event.stopPropagation()}
          >
            <div className="adminDeleteIcon" aria-hidden="true">
              !
            </div>
            <div>
              <p className="adminDeleteEyebrow">Confirm delete</p>
              <h2 id="deleteDialogTitle">Delete this car?</h2>
              <p id="deleteDialogText">
                {getCarTitle(carPendingDelete)} will be removed from the admin
                list and this action cannot be undone.
              </p>
            </div>
            <div className="adminDeleteActions">
              <button
                type="button"
                className="adminCancelDelete"
                onClick={closeDeleteDialog}
                disabled={isDeleting}
              >
                Cancel
              </button>
              <button
                type="button"
                className="adminConfirmDelete"
                onClick={deleteCar}
                disabled={isDeleting}
              >
                {isDeleting ? "Deleting..." : "Delete car"}
              </button>
            </div>
          </section>
        </div>
      )}
    </main>
  );
}

export default Admin;
